import React, { Fragment, useMemo } from "react";
import { ReactSVG } from "react-svg";
import { useDispatch, useSelector } from "react-redux";
import { areaGameCompleted, patchGameArea } from "../../actions/game.action";
import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";
import { generateOptions, getAreaMode, getDistanceIn } from "../../utils";
import RippleButton from "../buttons/RippleButton";

import { motion } from "framer-motion";

const FlagsPage = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { settings } = useSelector(({ user }) => user);

  const { area, countryOfToday } = useSelector(({ game }) => game);

  const handleGuess = (newGuess) => {
    if (newGuess === quizData.answer) {
      toast.success(t("Congratulations!"));
      dispatch(areaGameCompleted());
    }
    if (area.guesses.length < 4) {
      dispatch(
        patchGameArea({
          guesses: [...new Set([...area.guesses, newGuess])],
        }),
      );
    }
  };

  const quizData = useMemo(
    () => generateOptions(countryOfToday?.area, settings?.distanceUnit),
    [countryOfToday?.area, settings?.distanceUnit],
  );

  const choicesMap = useMemo(
    () =>
      quizData?.options?.map((item, idx) => ({
        value: item,
        label: "ABCDEFGHIJKLMNOP"[idx],
        mode: getAreaMode(
          item,
          area?.guesses,
          quizData?.answer,
          area.completed,
        ),
      })),
    [quizData, area],
  );

  return (
    <Fragment>
      <span
        className={`relative mt-4 w-full text-center font-bold text-slate-800 dark:text-slate-100 `}
      >
        {t("Do you know the area of", { country: countryOfToday?.name })}
      </span>
      <div className={`w-full`}>
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          className={`relative flex h-48 w-full  transform overflow-hidden rounded-lg p-4 transition-transform duration-500`}
        >
          <img
            className="pointer-events-none  m-auto h-full select-none transition-all duration-700 ease-in dark:invert"
            alt={"country to guess"}
            height="208"
            width="208"
            src={`/data/${countryOfToday?.Alpha2Code?.toLowerCase()}/map.svg`}
          />
          <div className="absolute inset-0 z-10 flex items-center justify-center text-slate-100">
            <ReactSVG
              className="w-16"
              src={`/images/sq_${settings.distanceUnit}.svg`}
            />
          </div>
        </motion.div>
      </div>
      <div className={`grid w-full grid-cols-2 gap-2`}>
        {choicesMap?.map((item, idx) => (
          <motion.div
            key={`svg-button-${idx}`}
            initial={{ opacity: 0, translateY: 20 }}
            animate={{ opacity: 1, translateY: 0 }}
            transition={{ delay: idx * 0.1 }}
          >
            <RippleButton
              className={`transition-color min-h-16 w-full cursor-pointer items-start justify-center overflow-hidden border-2 border-slate-300 bg-slate-200 p-2 pl-4 pr-2 text-left font-bold dark:border-slate-400  dark:bg-slate-600 ${
                [
                  " !dark:bg-majky-700 !dark:border-majky-500   !border-majky-600 !bg-majky-200 !text-majky-600 dark:!bg-majky-950 ",
                  " !dark:bg-red-600 !dark:border-red-400   !border-red-600 !bg-red-200 !text-red-600 dark:!bg-red-950 ",
                  " !dark:bg-blue-600 !dark:border-blue-400   !border-blue-600 !bg-blue-200 !text-blue-600 dark:!bg-blue-950 ",
                ][item.mode]
              } select-none rounded-md hover:mr-0 hover:bg-blue-600 hover:font-bold hover:text-slate-100 dark:hover:!bg-blue-950 ${
                (area.completed || item.mode !== -1) && " pointer-events-none "
              } select-none`}
              onClick={() => {
                handleGuess(item.value);
              }}
            >
              <span className="mr-2">{item.label}.</span> {item.value}
            </RippleButton>
          </motion.div>
        ))}
      </div>
      {area.completed && (
        <div className="my-1 flex w-full flex-col items-center justify-center">
          <p className="my-0 flex w-auto  flex-row items-center  justify-center space-x-3 rounded-lg border border-slate-500 p-2 px-10 text-base">
            <span>Area:</span>
            <span className="text-xl font-bold">
              {getDistanceIn(countryOfToday.area, settings.distanceUnit, 2)}
            </span>
          </p>
        </div>
      )}
    </Fragment>
  );
};

export default FlagsPage;
